import React, { useState, useMemo, ChangeEvent, useEffect, useContext } from 'react';
import {
    Box,
    styled,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TableCell as MuiTableCell,
    Checkbox,
    Typography,
    Grid
} from '@mui/material';
import WeeklyReportModal from "./WeeklyReportModal";
import reportApi from '../../../utils/api/report';
import FormModal from '../../Shared/form/modal/FormModal';
import { FormContext, FormSchemaFields } from "@sw-sw/lib-form";
import { FormContextProvider } from '@sw-sw/lib-form';
import AppDivisionContext from '../../../contexts/AppDivisionContext';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import ExcelJs from 'exceljs';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import { formatMMDDDateWithoutTimezone } from '../../../utils/date';

const StyledTableCell = styled(MuiTableCell)(() => ({
    [`&.${MuiTableCell}`]: {
        fontSize: 14,
        color: 'rgb(80, 101, 126)',
    },
}));

const headCells = [
    { id: 'projectName', label: 'Project Name', isEnable: true, showOption: false, haveSubHeader: false, subHeader: [], showInOptions: false },
    { id: 'city', label: 'City', isEnable: true, showOption: false, haveSubHeader: false, subHeader: [], showInOptions: true },
    { id: 'workOrderNumber', label: 'Work Order Number', isEnable: true, showOption: false, haveSubHeader: false, subHeader: [], showInOptions: true },
    { id: 'purchaseOrderNumber', label: 'Purchase Order Number', isEnable: true, showOption: false, haveSubHeader: false, subHeader: [], showInOptions: true },
    { id: 'primaryContact', label: 'Primary Contact', isEnable: true, showOption: false, haveSubHeader: false, subHeader: [], showInOptions: true },
    { id: 'permitStatus', label: 'Permit Status', isEnable: true, showOption: false, haveSubHeader: false, subHeader: [], showInOptions: true },
    { id: 'frequency', label: 'Frequency', isEnable: true, showOption: false, haveSubHeader: false, subHeader: [], showInOptions: true },
    {
        id: 'weekday',
        label: 'Weekday',
        isEnable: true,
        showOption: false,
        haveSubHeader: true,
        subHeader: [],
        showInOptions: false
    },
    { id: 'inspectionNotes', label: 'Inspection Notes', isEnable: true, showOption: false, haveSubHeader: false, subHeader: [], showInOptions: true },
    { id: 'additionalNotes', label: 'Project Notes', isEnable: true, showOption: false, haveSubHeader: false, subHeader: [], showInOptions: true },
];

const getDownloadSchema = () => {

    const schema: any = {
        download: {
            controlType: UIControlType.radio,
            options: [
                { value: '1', label: 'Download data avalible on screen' },
                { value: '2', label: 'Download all projects' },
            ],
        }
    };

    return schema;
};

interface DataRow {
    [key: string]: string | number | boolean | null | undefined | { [key: string]: any } | any[];
    inspections?: { created_date: string; inspection_types: {type:string}[] }[];
    address?: { city?: string };
    primary_contact?: { first_name: string; last_name: string };
    notes?: string;
    inspection_interval?: { interval: string };
    work_order?: string;
    purchase_order?: string;
    cgp_tracking_id?: boolean;
}

const buildHeadCellsWithDynamicWeekdays = (rowName: any[], start_date: any, end_date: any) => {
    const dateSet = [];

    const startDate = new Date(start_date);
    const endDate = new Date(end_date);

    for (let date = startDate; date <= endDate; date.setDate(date.getDate() + 1)) {
        const adjustedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

        dateSet.push(adjustedDate.toISOString().slice(0, 10)); // YYYY-MM-DD format
    }

    const sortedDates = Array.from(dateSet).sort(); 

    const subHeader = sortedDates.map((date) => ({
        id: date,
        label: date.split('-')[2].padStart(2, '0'),
    }));

    const dynamicHeadCells = rowName.map((cell) => {
        if (cell.id === 'weekday') {
            return {
                ...cell,
                subHeader,
            };
        }

        return cell;
    });

    return dynamicHeadCells;
};

function EnhancedTableHead({
    rowName,
    order,
    orderBy,
    onRequestSort,
    monthLabel,
}: {
    rowName: { id: string; label: string; isEnable: boolean; showOption: boolean; haveSubHeader: boolean; subHeader: { id: string; label: string; }[]; showInOptions: boolean; }[];
    order: 'asc' | 'desc';
    orderBy: string;
    onRequestSort: (event: any, property: keyof DataRow) => void;
    monthLabel: string;
}) {
    const createSortHandler =
        (property: keyof DataRow) => (event: React.MouseEvent<unknown>) => {
            onRequestSort(event, property);
        };

    return (
        <TableHead>
            <TableRow>
                {rowName
                    .filter((cell) => cell.isEnable)
                    .map((headCell) => (
                        <StyledTableCell
                            key={headCell.id}
                            colSpan={headCell.haveSubHeader ? headCell.subHeader.length : 1}
                            rowSpan={headCell.haveSubHeader ? 1 : 2}
                            align="center"
                            sortDirection={orderBy === headCell.id ? order : false}
                        >
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={createSortHandler(headCell.id as keyof DataRow)}
                                sx={{
                                    color: orderBy === headCell.id ? 'red' : 'inherit'
                                }}
                            >
                                {headCell.label === 'Weekday' ? monthLabel : headCell.label}
                            </TableSortLabel>
                        </StyledTableCell>
                    ))}
            </TableRow>
            <TableRow>
                {rowName
                    .filter((cell) => cell.isEnable && cell.haveSubHeader)
                    .flatMap((cell: any) =>
                        cell.subHeader.map((sub: any) => (
                            <StyledTableCell key={sub.id} align="center">
                                {sub.label}
                            </StyledTableCell>
                        ))
                    )}
            </TableRow>
        </TableHead>
    );
}

export const WeeklyReport = () => {
    const [data, setData] = useState<DataRow[]>([]);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState<any>('projectName');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showModal, setShowModal] = useState(false);
    const [open, setOpen] = useState<boolean>(false);
    const [rowName, setRowName] = useState(headCells);
    const [search, setSearch] = useState('');
    const [count, setCount] = useState(0);
    const [showDownloadModal, setShowDownloadModal] = useState(false);

    const divisionId: any = useContext(AppDivisionContext).appDivisionId;
    const formContext = useContext(FormContext);

    useEffect(() => {
        if (formContext.value.clients) {
            getData();
        }
    }, [rowsPerPage, page]);

    const getData = () => {
        reportApi
            .getWeeklyReport({
                clientIds: formContext.value.clients,
                projectIds: formContext.value.projects && formContext.value.projects.length && formContext.value.projects.map((p: any) => p.id),
                minDate: formContext.value.startDate,
                maxDate: formContext.value.endDate,
                divisionId: formContext.value.divisionId,
                showAll: formContext.value.showProjectsWithoutInspections,
                searchQuery: search,
                page: page + 1,
                limit: rowsPerPage,
            })
            .then((weeklyReport) => {
                setData(weeklyReport.pageData);
                setCount(weeklyReport.paginationData.numModels);
            });
    }

    const onCheck = (ele: { id: string }) => {
        const newOption = rowName.map((row) => {
            if (ele.id === row.id) {
                row.isEnable = !row.isEnable;
            }

            return row;
        });

        setRowName(newOption);
    };

    const getInspectionNotes = (inspections: any) => {
        if (!inspections || !Array.isArray(inspections)) return '—';

        const notesArray: string[] = inspections
          .sort(
            (a: any, b: any) =>
              new Date(a.created_date).getTime() -
              new Date(b.created_date).getTime(),
          )
          .map((inspection) => {
            const date = formatMMDDDateWithoutTimezone(inspection.created_date);
            const types = inspection.inspection_types
              ?.map((typeObj: any) => {
                switch (typeObj.type) {
                  case 'Routine':
                    return 'R';
                  case 'Routine + Post Storm':
                    return 'R+PS';
                  case 'Post Storm':
                    return 'PS';
                  case 'Other':
                    return 'O';
                  case 'Final':
                    return 'F';
                  case 'Pre-Qualifying Precipitation Event':
                    return 'PrQE';
                  case 'During Qualifying Precipitation Event':
                    return 'DQE';
                  case 'Post-Qualifying Precipitation Event':
                    return 'PoQE';
                  case 'Before Release of Contained Storm Water':
                    return 'BRC';
                  case 'After Release of Contained Storm Water':
                    return 'DRC';
                  case 'Quarterly Non-Storm Water':
                    return 'QNSW';
                  default:
                    return typeObj.type;
                }
              })
              .join(', ');

            let noteContent = '';

            if (inspection.certification_notes) {
              noteContent = inspection.certification_notes;
            } else if (inspection.notes && Array.isArray(inspection.notes)) {
              const contentNote = inspection.notes.find(
                (note: any) => note.content,
              );
              if (contentNote) {
                noteContent = contentNote.content;
              }
            }

            return noteContent ? `${date} ${types} - ${noteContent}` : '';
          });

        return notesArray.filter((note) => note).join('; ') || '—';
    };

    const handleExcelDownload = async () => {
        const isClientSide = formContext.value.download === '1';
        const workbook = new ExcelJs.Workbook();
        const worksheet = workbook.addWorksheet('Weekly Report');

        const Headers = buildHeadCellsWithDynamicWeekdays(rowName, formContext.value.startDate, formContext.value.endDate);
        const enabledHeaders = Headers.filter((cell) => cell.isEnable);
        const monthLabel = formContext.value.startDate
            ? new Date(formContext.value.startDate).toLocaleString('default', { month: 'long', year: 'numeric' })
            : '';

        const flatHeaders: { header: string; key: string; width?: number }[] = [];

        enabledHeaders.forEach((cell) => {
            if (cell.haveSubHeader) {
                cell.subHeader.forEach((sub: { label: string; id: string }) => {
                    flatHeaders.push({
                        header: sub.label,
                        key: `${cell.id}_${sub.id}`,
                        width: 15,
                    });
                });
            } else {
                flatHeaders.push({
                    header: cell.label,
                    key: cell.id,
                    width: 20,
                });
            }
        });
        worksheet.columns = flatHeaders;

        // 🧩 Setup Header Rows
        const topHeaderRow = worksheet.getRow(1);
        const subHeaderRow = worksheet.getRow(2);
        let currentCol = 1;

        enabledHeaders.forEach((cell) => {
            if (cell.haveSubHeader && cell.subHeader.length) {
                const colSpan = cell.subHeader.length;

                worksheet.mergeCells(1, currentCol, 1, currentCol + colSpan - 1);
                topHeaderRow.getCell(currentCol).value = monthLabel;
                topHeaderRow.getCell(currentCol).alignment = { horizontal: 'center' };

                cell.subHeader.forEach((sub: any, idx: any) => {
                    subHeaderRow.getCell(currentCol + idx).value = sub.label;
                });

                currentCol += colSpan;
            } else {
                worksheet.mergeCells(1, currentCol, 2, currentCol);
                topHeaderRow.getCell(currentCol).value = cell.label;
                topHeaderRow.getCell(currentCol).alignment = { horizontal: 'center' };
                currentCol++;
            }
        });

        topHeaderRow.font = { bold: true };
        subHeaderRow.font = { bold: true };

        const fetchData = isClientSide
            ? Promise.resolve(data)
            : reportApi.getWeeklyReport({
                clientIds: formContext.value.clients,
                minDate: formContext.value.startDate,
                maxDate: formContext.value.endDate,
                divisionId: formContext.value.divisionId,
                showAll: formContext.value.showProjectsWithoutInspections,
                page: 1,
                limit: 1000000,
            }).then((res) => res.pageData);

        const rows = await fetchData;

        rows.forEach((row: DataRow) => {
            const excelRow: DataRow = {};

            enabledHeaders.forEach((cell) => {
                if (cell.haveSubHeader) {
                    cell.subHeader.forEach((sub: any) => {
                        const match = Array.isArray(row.inspections)
                            ? row.inspections.find((i) =>
                                new Date(i.created_date).toISOString().slice(0, 10) === sub.id
                            )
                            : undefined;

                        const getAbbreviation = (type: string) => {
                            switch (type) {
                                case 'Routine': return 'R';
                                case 'Routine + Post Storm': return 'R+PS';
                                case 'Post Storm': return 'PS';
                                case 'Other': return 'O';
                                case 'Final': return 'F';
                                case 'Pre-Qualifying Precipitation Event': return 'PrQE';
                                case 'During Qualifying Precipitation Event': return 'DQE';
                                case 'Post-Qualifying Precipitation Event': return 'PoQE';
                                case 'Before Release of Contained Storm Water': return 'BRC';
                                case 'After Release of Contained Storm Water': return 'DRC';
                                case 'Quarterly Non-Storm Water': return 'QNSW';
                                default: return type;
                            }
                        };                    

                        excelRow[`${cell.id}_${sub.id}`] = match ? (`${match.inspection_types?.map((inspection_types: any) => getAbbreviation(inspection_types.type)).join(', ')}-${formatMMDDDateWithoutTimezone(match.created_date)}` || '') : '—';
                    });
                } else {
                    switch (cell.id) {
                        case 'projectName':
                            excelRow[cell.id] = row.name;
                            break;
                        case 'city':
                            excelRow[cell.id] = row.address?.city || '—';
                            break;
                        case 'primaryContact':
                            excelRow[cell.id] = row.primary_contact || '—';
                            break;
                        case 'additionalNotes':
                            excelRow[cell.id] = row.notes || '—';
                            break;
                        case 'inspectionNotes':
                            excelRow[cell.id] = getInspectionNotes(row.inspections);
                            break;
                        case 'frequency':
                            excelRow[cell.id] = row.inspection_interval?.interval ?? '—';
                            break;
                        case 'workOrderNumber':
                            excelRow[cell.id] = row.work_order || '—';
                            break;
                        case 'purchaseOrderNumber':
                            excelRow[cell.id] = row.purchase_order || '—';
                            break;
                        case 'permitStatus':
                            excelRow[cell.id] = row.cgp_tracking_id ? 'P' : 'NP';
                            break;
                        default:
                            excelRow[cell.id] = row[cell.id] ?? '—';
                    }
                }
            });

            worksheet.addRow(excelRow);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');

        a.href = url;
        a.download = `Weekly_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);

        setShowDownloadModal(false);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await reportApi.getWeeklyReportModalData(divisionId);

                formContext.set("modalData", response.data);
                formContext.set("divisionId", divisionId);
                setShowModal(true)

            } catch (error) {
                console.error("Error fetching weekly report data:", error);
            }
        };

        fetchData()
    }, []);

    const handleRequestSort = (_event: unknown, property: keyof DataRow) => {
        const isAsc = orderBy === property && order === 'asc';

        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        if (parseInt(event.target.value, 10) === -1) {
            setRowsPerPage(count)
            setPage(0)
        } else {
            setRowsPerPage(parseInt(event.target.value, 10))
            setPage(0);
        }
    };

    const handleChangeSearch = async (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        setSearch(value);

        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
        }

        debounceTimeout = setTimeout(() => {
            getData();
        }, 300);
    };

    let debounceTimeout: NodeJS.Timeout | null = null;

    const visibleRows = useMemo(
        () => {
            const sortedData = _.orderBy(data, [orderBy], [order]);

            return sortedData.slice(0, rowsPerPage);
        },
        [data, order, orderBy, rowsPerPage]
    );

    const dynamicHeadCells = buildHeadCellsWithDynamicWeekdays(rowName, formContext.value.startDate, formContext.value.endDate);

    if (showDownloadModal) {
        return (
            <FormModal
                onSubmit={handleExcelDownload}
                onCancel={() => setShowDownloadModal(false)}
                modalProps={{
                    title: "Download Weekly Report",
                    submitBtnText: 'Generate',
                }}
            >
                <section className='weekly-report-form'>
                    <section className='container'>
                        <FormSchemaFields
                            schema={getDownloadSchema()}
                            onChange={formContext.set}
                            formData={formContext.value}
                            initialFormData={formContext.value}
                        />
                    </section>
                </section>
            </FormModal>
        );
    }

    return (
        <FormContextProvider>
            <Box sx={{ paddingLeft: "5px" }} className='weekly-report'>
                <div className='weekly-report__menu'>
                    {data.length ? <div
                        className='weekly-report__cog-wrapper'
                        onClick={() => setOpen(!open)}
                    >
                        <FontAwesomeIcon icon={faCog} className='pad-left' />
                    </div> : null}
                    <div
                        className='weekly-report__drawer-wrapper'
                        style={{ display: open ? 'block' : 'none' }}
                    >
                        <div className='weekly-report__drawer'>
                            <p>Column Setting</p>
                            <hr />
                            <div className='weekly-report__widget-list'>
                                {dynamicHeadCells && dynamicHeadCells.length
                                    ? dynamicHeadCells.map((row: any) => {
                                        if (row.showInOptions) {
                                            return (
                                                <div className='weekly-report__widget-list-item'>
                                                    <Checkbox
                                                        onChange={() => onCheck(row)}
                                                        checked={row.isEnable}
                                                    />
                                                    <Typography variant='h6'>{row.label}</Typography>
                                                </div>
                                            );
                                        }

                                        return null;
                                    })
                                    : null}
                            </div>
                        </div>
                    </div>
                </div>
                <div className='weekly-report__toolbar'>
                    <Grid container spacing={2} alignItems="center" sx={{ width: '100%' }}>
                        <Grid item xs={12} sm={6} md={8}>
                            <input
                                type='text'
                                placeholder='Search'
                                onChange={handleChangeSearch}
                                value={search}
                                className='selectClient-input search-control'
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} container spacing={1} justifyContent="flex-end">
                            <Grid item xs={6}>
                                <button className='primary' style={{ width: '100%' }} onClick={() => setShowModal(true)}>
                                    Modify Report
                                </button>
                            </Grid>
                            <Grid item xs={6}>
                                <button className='primary' style={{ width: '100%' }} onClick={() => setShowDownloadModal(true)}>
                                    Download
                                </button>
                            </Grid>
                        </Grid>
                    </Grid>
                </div>
                {data &&
                    <>
                        <TableContainer className='weekly-report__table-container'>
                            <Table>
                                <EnhancedTableHead
                                    rowName={dynamicHeadCells}
                                    order={order}
                                    orderBy={orderBy}
                                    onRequestSort={handleRequestSort}
                                    monthLabel={new Date(formContext.value.startDate).toLocaleString('default', { month: 'long', year: 'numeric' }) || "Month Year"}
                                />
                                <TableBody>
                                    {visibleRows.map((row: any, idx: number) => (
                                        <TableRow key={idx}>
                                            {dynamicHeadCells.map((cell) => {
                                                if (!cell.isEnable) return null;

                                                if (cell.haveSubHeader && Array.isArray(cell.subHeader)) {
                                                    return cell.subHeader.map((sub: any) => {
                                                        const inspection = row.inspections?.find(
                                                            (i: any) => i.created_date === sub.id
                                                        );
                                                        const getAbbreviation = (type: string) => {
                                                            switch (type) {
                                                                case 'Routine': return 'R';
                                                                case 'Routine + Post Storm': return 'R+PS';
                                                                case 'Post Storm': return 'PS';
                                                                case 'Other': return 'O';
                                                                case 'Final': return 'F';
                                                                case 'Pre-Qualifying Precipitation Event': return 'PrQE';
                                                                case 'During Qualifying Precipitation Event': return 'DQE';
                                                                case 'Post-Qualifying Precipitation Event': return 'PoQE';
                                                                case 'Before Release of Contained Storm Water': return 'BRC';
                                                                case 'After Release of Contained Storm Water': return 'DRC';
                                                                case 'Quarterly Non-Storm Water': return 'QNSW';
                                                                default: return type;
                                                            }
                                                        };
                                                        const value = inspection ? (`${inspection.inspection_types?.map((inspection_types: any) => getAbbreviation(inspection_types.type)).join(', ')}-${formatMMDDDateWithoutTimezone(inspection.created_date)}` || '') : '—';

                                                        return (
                                                            <StyledTableCell key={`${cell.id}-${sub.id}`} sx={{ whiteSpace: 'nowrap' }}>
                                                                <strong>{value}</strong>
                                                            </StyledTableCell>
                                                        );
                                                    });
                                                }

                                                let value;

                                                switch (cell.id) {
                                                    case 'projectName':
                                                        value = row.name;
                                                        break;
                                                    case 'city':
                                                        value = row.address?.city || '—';
                                                        break;
                                                    case 'primaryContact':
                                                        value = row.primary_contact || '—';
                                                        break;
                                                    case 'additionalNotes':
                                                        value = row.notes || '—';
                                                        break;
                                                    case 'inspectionNotes':
                                                        value = getInspectionNotes(row.inspections);
                                                        break;
                                                    case 'frequency':
                                                        value = row.inspection_interval.interval || '—';
                                                        break;
                                                    case 'workOrderNumber':
                                                        value = row.work_order || '—';
                                                        break;
                                                    case 'purchaseOrderNumber':
                                                        value = row.purchase_order || '—';
                                                        break;
                                                    case 'permitStatus':
                                                        value = row.cgp_tracking_id ? 'P' : 'NP';
                                                        break;
                                                    default:
                                                        value = row[cell.id] ?? '—';
                                                }

                                                return <StyledTableCell key={cell.id}>{value}</StyledTableCell>;
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>

                            </Table>
                        </TableContainer>
                        <TablePagination
                            labelRowsPerPage="Rows per page:"
                            rowsPerPageOptions={[10, 25, 50, { value: -1, label: 'All' }]}
                            component='div'
                            count={count}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </>
                }
                {showModal && (
                    <WeeklyReportModal
                        setShowModal={setShowModal}
                        setData={setData}
                        setCount={setCount}
                        formData={formContext.value}
                        setFormData={formContext.set}
                        isEdit={false}
                    />
                )}
            </Box>
        </FormContextProvider>
    );
};
